
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const TermsAndConditionsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold mb-2">Terms and Conditions</h1>
          <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <Card>
          <CardContent className="p-8 prose prose-gray dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Acceptance of Terms</h2>
              <p>
                By accessing and using GoDyspo's platform, you accept and agree to be bound by the terms and provision of this agreement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Description of Service</h2>
              <p>
                GoDyspo is a real estate investment platform that connects property sellers (wholesalers and agents) with potential buyers (investors). We provide tools for property listing, searching, and communication.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">User Accounts</h2>
              <ul className="list-disc pl-6">
                <li>You must provide accurate and complete information when creating an account</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                <li>You must be at least 18 years old to use our services</li>
                <li>One person or entity may not maintain more than one account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Acceptable Use</h2>
              <p className="mb-4">You agree not to:</p>
              <ul className="list-disc pl-6">
                <li>Use the platform for any illegal or unauthorized purpose</li>
                <li>Post false, misleading, or fraudulent property information</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated systems to access our platform without permission</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Property Listings</h2>
              <h3 className="text-lg font-medium mb-2">For Sellers:</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>You warrant that all property information is accurate and current</li>
                <li>You have the legal right to sell or assign the properties listed</li>
                <li>You are responsible for all property descriptions and images</li>
              </ul>
              
              <h3 className="text-lg font-medium mb-2">For Buyers:</h3>
              <ul className="list-disc pl-6">
                <li>You acknowledge that all investments carry risk</li>
                <li>You are responsible for conducting your own due diligence</li>
                <li>GoDyspo does not guarantee the accuracy of property information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Subscription and Payments</h2>
              <ul className="list-disc pl-6">
                <li>Buyer accounts are free</li>
                <li>Subscriptions automatically renew unless cancelled</li>
                <li>Refunds are subject to our refund policy</li>
                <li>We reserve the right to change pricing with notice</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">SMS and Messaging Communications Consent</h2>
              
              <h3 className="text-lg font-medium mb-2">Overview</h3>
              <p className="mb-4">
                By creating an account and providing your mobile phone number, you consent to receive SMS text messages from GoDyspo. 
                These messages may include transactional notifications, property alerts, user-to-user communications, and other platform-related 
                messages to enhance your experience. Marketing messages require separate explicit opt-in.
              </p>

              <h3 className="text-lg font-medium mb-2">Types of Messages You May Receive</h3>
              <p className="mb-2">GoDyspo may send the following types of messages:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>
                  <strong>Transactional Messages:</strong> Account verification codes, two-factor authentication (2FA), security alerts, 
                  password reset confirmations, and other account-related notifications essential to your use of the platform.
                </li>
                <li>
                  <strong>Platform Communications:</strong> New property matches based on your saved searches, notifications when followed 
                  sellers list new properties, system updates, maintenance notifications, and important platform announcements.
                </li>
                <li>
                  <strong>User-to-User Messages:</strong> Direct communications between buyers and sellers, inquiry notifications, 
                  responses to property questions, and other peer-to-peer interactions facilitated through the platform.
                </li>
                <li>
                  <strong>Marketing Messages:</strong> Promotional offers, new feature announcements, special deals, and platform updates 
                  (requires separate explicit opt-in and can be managed independently).
                </li>
                <li>
                  <strong>Customer Support:</strong> Responses to your support inquiries, assistance with technical issues, and 
                  follow-up communications related to your requests for help.
                </li>
              </ul>

              <h3 className="text-lg font-medium mb-2">Consent Collection</h3>
              <p className="mb-2">Your consent to receive text messages is collected through:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Web forms during account registration where you provide your phone number</li>
                <li>Checkbox opt-in during the signup process acknowledging SMS communications</li>
                <li>Separate explicit consent checkbox for marketing communications</li>
                <li>In-app prompts when enabling specific notification preferences</li>
              </ul>
              <p className="mb-4">
                By providing your phone number and checking the SMS consent box, you agree to receive text messages at the number provided. 
                You certify that you are authorized to receive text messages at this number.
              </p>

              <h3 className="text-lg font-medium mb-2">Message Frequency and Costs</h3>
              <p className="mb-2">
                Message frequency varies based on your account activity and preferences:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Transactional messages:</strong> 1-10 per month (as needed for security and account management)</li>
                <li><strong>Platform communications:</strong> 5-20 per month (varies based on your saved searches and followed sellers)</li>
                <li><strong>User-to-user messages:</strong> Frequency varies based on your interactions with other users</li>
                <li><strong>Marketing messages:</strong> Up to 4 per month (only if you opt in separately)</li>
              </ul>
              <p className="mb-4">
                <strong>Message and data rates may apply</strong> as charged by your mobile carrier. GoDyspo does not charge for text messages, 
                but standard carrier rates for SMS and data usage will apply according to your mobile plan. Check with your carrier for details.
              </p>

              <h3 className="text-lg font-medium mb-2">How to Opt-Out</h3>
              <p className="mb-2">
                You can stop receiving text messages from GoDyspo at any time:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Reply <strong>STOP</strong> to any text message to unsubscribe from all non-essential messages</li>
                <li>Reply <strong>HELP</strong> for assistance or customer support information</li>
                <li>Manage your notification preferences in your account settings</li>
                <li>Contact customer support at support@godyspo.com to adjust your preferences</li>
              </ul>
              <p className="mb-4">
                <strong>Important:</strong> Opting out of text messages may affect your ability to receive important account notifications 
                and time-sensitive property alerts. Essential security messages (like 2FA codes) may still be sent even if you opt out of 
                other message types.
              </p>

              <h3 className="text-lg font-medium mb-2">Compliance and Your Rights</h3>
              <p className="mb-2">
                GoDyspo complies with the Telephone Consumer Protection Act (TCPA) and all applicable telecommunications regulations:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Your consent to receive text messages is <strong>not a condition of purchase</strong> or use of GoDyspo's services</li>
                <li>Marketing messages require separate, explicit opt-in and can be managed independently from transactional messages</li>
                <li>You have the right to withdraw consent at any time without penalty</li>
                <li>We will not share your phone number with third parties for their marketing purposes without your explicit consent</li>
                <li>All messaging practices comply with applicable federal and state privacy laws</li>
              </ul>
              <p className="mb-4">
                For more information about how we collect, use, and protect your phone number and messaging data, 
                please review our Privacy Policy.
              </p>

              <h3 className="text-lg font-medium mb-2">Contact Information for Messaging Support</h3>
              <p className="mb-4">
                If you have questions about text messaging or need assistance:
              </p>
              <ul className="list-disc pl-6">
                <li>Email: support@godyspo.com</li>
                <li>Reply <strong>HELP</strong> to any text message</li>
                <li>Visit your account settings to manage preferences</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Disclaimer of Warranties</h2>
              <p>
                Our platform is provided "as is" without warranties of any kind. We do not guarantee the accuracy, completeness, or reliability of any property information or investment outcomes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Limitation of Liability</h2>
              <p>
                GoDyspo shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our platform or any property transactions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Termination</h2>
              <p>
                We may terminate or suspend your account at any time for violation of these terms or for any other reason at our sole discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Governing Law</h2>
              <p>
                These terms are governed by the laws of [State/Country] without regard to conflict of law principles.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
              <p>
                For questions about these Terms and Conditions, please contact us at:
              </p>
              <p className="mt-2">
                Email: legal@godyspo.com<br />
                Address: [Company Address]
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
